import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DatePicker, Button, Tag, ConfigProvider, theme } from 'antd';
import dayjs from 'dayjs';
import { getWebActivityLogoUrl, getWebRTDSUrl } from '../webBridge';
import '../assets/RTDS.css';

type RTDSMatch = {
    id: string;
    bout_number: string;
    category: string;
    round: string;
    round_number: number;
    state: string;
    game_round: number;
    short_round: string;
    score: {
        [key: string]: number;
    };
    round_score: {
        [key: string]: {
            [key: number]: number;
        };
    };
    winner_id: string;
    participants: {
        id: string;
        display_name: string;
        noc: string;
        team: string;
    }[];
}

type RTDSMatchUpdate = {
     id: string;
     court: number;
     state: string;
    score: {
        [key: string]: number;
    };
    round_score: {
        [key: string]: {
            [key: number]: number;
        };
    };
     round_number: number;
     winner_id:string;
     game_round: number;
}


// 新增工具函数：获取旗帜图片
function getFlagUrl(noc: string | undefined) {
    if (!noc) return '/noc/placeholder.png';
    return `/noc/${noc}.png`;
}

// 新增 FlagImage 组件
interface FlagImageProps {
    noc: string | undefined;
    className?: string;
}

function FlagImage({ noc, className = '' }: FlagImageProps) {
    return (
        <span className="flag-container">
            <img 
                className={`flag-img ${className}`}
                src={getFlagUrl(noc)} 
                alt={noc} 
            />
            <span className="noc-code">{noc || ''}</span>
        </span>
    );
}

// 判断名称是否为 TBD（或为空/缺失）
function isNameTBD(name?: string) {
    if (!name) return true;
    const trimmed = name.trim();
    if (!trimmed) return true;
    return trimmed.toLowerCase() === 'tbd';
}

// TOTAL 显示局分。Walkover 在 Match 服务里写成赢 1、输 0。
export function getRTDSParticipantTotal(
    match: Pick<RTDSMatch, 'score' | 'state' | 'winner_id'>,
    participantId?: string,
): number {
    if (!participantId) {
        return 0;
    }

    const stored = match.score?.[participantId];
    if (stored !== undefined) {
        return stored;
    }

    if (match.state === 'walkover' && match.winner_id) {
        return match.winner_id === participantId ? 1 : 0;
    }

    return 0;
}

// 横版表格行组件（旗帜和NOC缩写垂直排列在选手名左侧）
function MatchTableRow({ match }: { match: RTDSMatch }) {
    const left = match.participants[0];
    const right = match.participants[1];
    const leftScore = getRTDSParticipantTotal(match, left?.id);
    const rightScore = getRTDSParticipantTotal(match, right?.id);
    const leftRoundScore = match.round_score ? match.round_score[left.id] : {};
    const rightRoundScore = match.round_score ? match.round_score[right.id] : {};
    const roundKeys = Object.keys(leftRoundScore)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b);

    const renderRoundScore = (playerScore: number | undefined, round: number) => {
        if (leftRoundScore[round] === undefined) {
            return '-';
        }

        const hasStarted = leftRoundScore[round] === 1 ||  rightRoundScore[round] === 1;
        if (!hasStarted) {
            return '0';
        }
        
        if (playerScore === 1) {
            return <img src="/WT-yellow.png" alt="Winner" style={{ height: '24px' }} />;
        } else if (playerScore === 0) {
            return 'L';
        }
        return '-';
    };

    return (
        <>
            <tr>
                <td rowSpan={2} className="cell-bout">{match.bout_number}</td>
                <td className="cell-player blue-bg">
                    <div className="player-info">
                        <FlagImage noc={left?.noc} />
                        <div className="player-name">{left?.display_name || 'TBD'}</div>
                    </div>
                </td>
                {roundKeys.map((r) => (
                    <td key={`l-${r}`} className="cell-round">{renderRoundScore(leftRoundScore[r], r)}</td>
                ))}
                <td className="cell-total">{leftScore}</td>
            </tr>
            <tr>
                <td className="cell-player red-bg">
                    <div className="player-info">
                        <FlagImage noc={right?.noc} />
                        <div className="player-name">{right?.display_name || 'TBD'}</div>
                    </div>
                </td>
                {roundKeys.map((r) => (
                    <td key={`r-${r}`} className="cell-round">{renderRoundScore(rightRoundScore[r], r)}</td>
                ))}
                <td className="cell-total">{rightScore}</td>
            </tr>
        </>
    );
}

// 横版表格组件
function MatchTable({ match }: { match: RTDSMatch }) {
    const left = match.participants[0];
    const leftRoundScore = match.round_score ? match.round_score[left.id] : {};
    const roundKeys = Object.keys(leftRoundScore)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b);

    return (
        <table className="match-table">
            <thead>
                <tr>
                    <th className="th-grey">BOUT</th>
                    <th className="th-grey">{match.round} ({match.category})</th>
                    {roundKeys.map((r) => (
                        <th key={`h-${r}`} className="th-grey">{`R${r}`}</th>
                    ))}
                    <th className="th-grey">TOTAL</th>
                </tr>
            </thead>
            <tbody>
                <MatchTableRow match={match} />
            </tbody>
        </table>
    );
}

// UPCOMING横向表格（旗帜和NOC缩写为一组，分别在选手名称左右两侧）
function UpcomingTable({ matches }: { matches: RTDSMatch[] }) {
    return (
        <table className="match-table upcoming-table">
            <colgroup>
                <col className="upcoming-col-bout" />
                <col className="upcoming-col-round" />
                <col className="upcoming-col-player" />
                <col className="upcoming-col-player" />
            </colgroup>
            <tbody>
                {matches.map(match => {
                    const p1 = match.participants[0];
                    const p2 = match.participants[1];
                    return (
                        <tr key={match.id}>
                            <td className="cell-bout">{match.bout_number}</td>
                            <td className="cell-round">{match.short_round} {match.category}</td>
                            <td className="cell-player blue-bg">
                                {isNameTBD(p1?.display_name) ? (
                                    <div className="player-info"></div>
                                ) : (
                                    <div className="player-info">
                                        <FlagImage noc={p1?.noc} />
                                        <div className="player-name">{p1?.display_name}</div>
                                    </div>
                                )}
                            </td>
                            <td className="cell-player red-bg">
                                {isNameTBD(p2?.display_name) ? (
                                    <div className="player-info"></div>
                                ) : (
                                    <div className="player-info">
                                        <div className="player-name">{p2?.display_name}</div>
                                        <FlagImage noc={p2?.noc} />
                                    </div>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

// 修改Court整体布局，Court Number在主表格和UPCOMING之间
function Court({ court, matches, inProgressRoundMaxByCategory }: { court: string, matches: RTDSMatch[], inProgressRoundMaxByCategory: Record<string, number> }) {
    // 当两位参赛者都为 TBD 或缺失时，才不展示该比赛
    const isMatchParticipantsReady = (m: RTDSMatch) => {
        const p1 = m?.participants?.[0]?.display_name;
        const p2 = m?.participants?.[1]?.display_name;
        return !(isNameTBD(p1) && isNameTBD(p2));
    };

    const matchesArray = Array.isArray(matches) ? matches : [matches];
    // 按状态和比赛编号排序
    const sortedMatches = [...matchesArray].sort((a, b) => {
        const stateOrder = { 'in_game': 0, 'finish': 1, 'walkover': 1, 'wait': 2, 'locked': 3 };
        const stateDiff = stateOrder[a.state as keyof typeof stateOrder] - stateOrder[b.state as keyof typeof stateOrder];
        if (stateDiff !== 0) return stateDiff;
        return parseInt(a.bout_number) - parseInt(b.bout_number);
    });
    const inGameMatch = sortedMatches.find(m => m.state === 'in_game' && isMatchParticipantsReady(m));
    const waitingMatches = sortedMatches.filter(m => (m.state === 'wait' || m.state === 'locked') && isMatchParticipantsReady(m));
    const lastFinishedMatch = !inGameMatch ? sortedMatches
        .slice()
        .reverse()
        .find(m => (
            (m.state === 'finish' || m.state === 'walkover') &&
            isMatchParticipantsReady(m) &&
            !(
                inProgressRoundMaxByCategory[m.category] !== undefined &&
                inProgressRoundMaxByCategory[m.category] > m.round_number
            )
        )) : null;

    const currentMatch = inGameMatch || lastFinishedMatch;
    const upcomingMatches = waitingMatches.slice(0, 3);
    return (
        <div className="court" style={{ display: 'flex', background: '#181818', borderRadius: 16, boxShadow: '0 2px 8px #0008', padding: 0 }}>
            {/* 左侧主表格 */}
            <div className="court-main" >
                {currentMatch && <MatchTable match={currentMatch} />}
            </div>
            {/* 中间大号Court Number */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 180 }}>
                <div className="court-title" style={{ fontSize: 60, color: '#fff', fontWeight: 900, letterSpacing: 2 }}> {court}</div>
            </div>
            {/* 右侧upcoming表格 */}
            <div className="court-upcoming">
                {upcomingMatches.length > 0 ? <UpcomingTable matches={upcomingMatches} /> : <div style={{ color: '#aaa', textAlign: 'center' }}>No upcoming matches</div>}
            </div>
        </div>
    );
}

// 获取星期几英文
function getWeekday(dateStr: string) {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    return d.format('dddd');
}

const RECONNECT_BASE_DELAY_MS = 3000;
const RECONNECT_MAX_DELAY_MS = 30000;
const RECONNECT_JITTER_MS = 500;

export function buildRTDSWebSocketUrl(apiUrl: string, date: string, baseUrl = window.location.href) {
    const url = new URL(apiUrl, baseUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if (date) {
        url.searchParams.set('date', date);
    }
    return url.toString();
}

export function RTDS() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // 使用 wsRef 管理连接实例，移除 ws 状态以避免未使用变量
    const wsRef = useRef<WebSocket | null>(null);
    const [status, setStatus] = useState<string>('disconnected');
    const [matches, setMatches] = useState<Record<string, RTDSMatch[]>>({});
    const [error, setError] = useState<string | null>(null);
    const [currentDate, setCurrentDate] = useState<string>(searchParams.get('date') || new Date().toISOString().split('T')[0]);
    const [currentActivityId] = useState<string>(searchParams.get('activity_id') || '');
    const [currentKey] = useState<string>(searchParams.get('key') || '');
    const [activityTitle, setAcivityTitle] = useState<string>('');
    const [activityLogo, setActivityLogo] = useState<string>('');

    // Reconnect handling
    const reconnectTimeoutRef = useRef<number | null>(null);
    const reconnectAttemptRef = useRef<number>(0);
    const isUnmountedRef = useRef<boolean>(false);
    // 当连接尚未 OPEN 时，缓存待发送的日期
    const pendingDateRef = useRef<string | null>(null);

    // const [lastCommunicationTime, setLastCommunicationTime] = useState<string | null>(null);

    const updateLastCommunicationTime = () => {
        // const now = new Date();
        // setLastCommunicationTime(now.toLocaleTimeString());
    };

    const connectWebSocket = useCallback(() => {
        if (!currentActivityId) {
            setError('PARAM ERROR: Activity ID is required');
            return;
        }
        if (!currentKey) {
            setError('PARAM ERROR: Key is required');
            return;
        }

        // Avoid duplicate connections when an existing ws is OPEN or CONNECTING
        const existing = wsRef.current;
        if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
            return;
        }

        setStatus('connecting');
        setError(null);

        const wsUrl = buildRTDSWebSocketUrl(
            getWebRTDSUrl(currentActivityId, currentKey),
            currentDate,
        );
        
        console.info(wsUrl);
        //const wsUrl = `ws://localhost:8096/rtds/ws?activity_id=${currentActivityId}&date=${currentDate}&key=${currentKey}`;
        const websocket = new WebSocket(wsUrl);

        const handleDisconnect = () => {
            // 旧连接的延迟回调不能清空或重连当前的新连接。
            if (wsRef.current !== websocket) return;

            wsRef.current = null;
            if (isUnmountedRef.current) return;
            reconnectRef.current();
        };

        websocket.onopen = () => {
            if (wsRef.current !== websocket || isUnmountedRef.current) return;

            setStatus('connected');
            updateLastCommunicationTime();
            reconnectAttemptRef.current = 0;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            setError(null);
            // 若有待更新的日期，连接建立后发送并清空
            if (pendingDateRef.current) {
                try {
                    websocket.send(JSON.stringify({
                        type: 'update_date',
                        date: pendingDateRef.current
                    }));
                    pendingDateRef.current = null;
                } catch (e) {
                    console.warn('Failed to send pending date after open:', e);
                }
            }
        };

        websocket.onmessage = (event) => {
            if (wsRef.current !== websocket || isUnmountedRef.current) return;

            updateLastCommunicationTime();
            let data: any;
            try {
                data = JSON.parse(event.data);
            } catch (e) {
                console.warn('Ignoring invalid RTDS WebSocket message:', e);
                return;
            }
            console.info("-->", data);
            if (data.type === 'title') {
                document.title = data.data.title + " - VTKD Portal";
                
                setAcivityTitle(data.data.title);

                if (data.data.logo) {
                    setActivityLogo(getWebActivityLogoUrl(currentActivityId));
                }
            }

            if (data.type === 'init' || data.type === 'update') {
                if (data.error) {
                    setError(data.error);
                } else if (data.data) {
                    if (data.type === 'update') {
                        const match = data.data as RTDSMatchUpdate;
                        setMatches(prevMatches => {
                            const newMatches = { ...prevMatches };
                            if (!newMatches[match.court]) {
                                newMatches[match.court] = [] as RTDSMatch[];
                            }
                            const courtMatches = [...newMatches[match.court]];
                            const matchIndex = courtMatches.findIndex(m => m.id === match.id);
                            if (matchIndex >= 0) {
                                courtMatches[matchIndex] = {
                                    ...courtMatches[matchIndex],
                                    ...match
                                };
                            } else {
                                console.error("no match update", match)
                                //courtMatches.push(match);
                            }

                            console.info(courtMatches)
                            newMatches[match.court] = courtMatches;
                            return newMatches;
                        });
                    } else {
                        setMatches(data.data);
                    }
                    setError(null);
                } else {
                    setMatches({});
                    setError('No matches scheduled for this date');
                }
            } else if (data.type === 'error') {
                setError(data.error);
            }
        };

        websocket.onclose = () => {
            handleDisconnect();
        };

        websocket.onerror = (error) => {
            if (wsRef.current !== websocket) return;

            console.error('WebSocket error:', error);
            try {
                websocket.close();
            } catch {}
            handleDisconnect();
        };

        wsRef.current = websocket;
    }, [currentActivityId, currentKey, currentDate]);

    const reconnectRef = useRef<() => void>(() => {});
    const connectWebSocketRef = useRef<() => void>(() => {});
    connectWebSocketRef.current = connectWebSocket;
    reconnectRef.current = () => {
        if (isUnmountedRef.current || reconnectTimeoutRef.current !== null) return;

        setStatus('connecting');
        const attempt = reconnectAttemptRef.current;
        const backoffDelay = Math.min(
            RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt),
            RECONNECT_MAX_DELAY_MS,
        );
        const delay = backoffDelay + Math.floor(Math.random() * RECONNECT_JITTER_MS);
        reconnectAttemptRef.current = attempt + 1;
        reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = null;
            if (!isUnmountedRef.current) {
                connectWebSocketRef.current();
            }
        }, delay);
    };

    useEffect(() => {
        isUnmountedRef.current = false;
        // 延迟到下一个事件循环，让 React Strict Mode 的首次探测挂载可以先清理，
        // 避免本地开发时为同一个页面真的建立两条 WebSocket。
        const initialConnectTimeout = window.setTimeout(() => {
            if (!isUnmountedRef.current) {
                connectWebSocketRef.current();
            }
        }, 0);

        return () => {
            isUnmountedRef.current = true;
            clearTimeout(initialConnectTimeout);
            if (reconnectTimeoutRef.current !== null) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            const existing = wsRef.current;
            wsRef.current = null;
            if (existing && existing.readyState !== WebSocket.CLOSED) {
                try {
                    existing.close();
                } catch {}
            }
        };
    }, []);

    const handleDateChange = (date: dayjs.Dayjs | null, dateString: string | string[]) => {
        const newDate = Array.isArray(dateString) ? dateString[0] : dateString;
        setCurrentDate(newDate);
        pendingDateRef.current = newDate;
        const existing = wsRef.current;
        if (existing && existing.readyState === WebSocket.OPEN) {
            try {
                existing.send(JSON.stringify({ type: 'update_date', date: newDate }));
                pendingDateRef.current = null;
            } catch (e) {
                console.warn('Failed to send update_date on OPEN ws:', e);
            }
        } else {
            // 连接尚未 OPEN，暂存日期，等 onopen 后发送
            pendingDateRef.current = newDate;
            setStatus('connecting');
        }
    };

    const getStatusTag = () => {
        const statusConfig = {
            connected: { color: 'success', text: 'Connected' },
            disconnected: { color: 'error', text: 'Disconnected' },
            error: { color: 'warning', text: 'Connection Error' },
            connecting: { color: 'blue', text: 'Connecting' }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const connectionIndicatorLabel = status === 'connecting'
        ? 'RTDS reconnecting'
        : 'RTDS disconnected';

    const handleReload = () => {
        const existing = wsRef.current;
        // 先移除所有权，再关闭旧连接；其延迟回调会被识别为过期回调。
        wsRef.current = null;
        if (existing && existing.readyState !== WebSocket.CLOSED) {
            try {
                existing.close();
            } catch {}
        }
        if (reconnectTimeoutRef.current !== null) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        reconnectAttemptRef.current = 0;
        setStatus('connecting');
        connectWebSocket();
    };

    // Set background color for the entire page
    useEffect(() => {
        document.documentElement.style.backgroundColor = '#111';
        document.body.style.backgroundColor = '#111';
        
        return () => {
            document.documentElement.style.backgroundColor = '';
            document.body.style.backgroundColor = '';
        };
    }, []);

    const inProgressRoundMaxByCategory = useMemo(() => {
        const maxMap: Record<string, number> = {};
        Object.values(matches).forEach(ms => {
            ms.forEach(m => {
                if (m.state === 'in_game' || m.state === 'finish' || m.state === 'walkover') {
                    const current = maxMap[m.category];
                    if (current === undefined || m.round_number > current) {
                        maxMap[m.category] = m.round_number;
                    }
                }
            });
        });
        return maxMap;
    }, [matches]);

    return (
        <div className="rtds-container">
            <div className="header" style={{ 
                position: 'relative', 
                width: '100%', 
                marginBottom: '24px', 
                display: 'grid', 
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center'
            }}>
                {/* Left Section - Logo */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {activityLogo && (
                        <img 
                            src={activityLogo} 
                            alt="Activity Logo" 
                            style={{ height: '40px', objectFit: 'contain' }} 
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                            }}
                        />
                    )}
                </div>

                {/* Center Section - Title */}
                <h1 style={{ 
                    margin: 0, 
                    textAlign: 'center',
                    gridColumn: 2,
                    justifySelf: 'center'
                }}>
                    {activityTitle}
                </h1>

                {/* Right Section - Controls */}
                {status !== 'connected' && (
                    <span
                        className={`rtds-connection-indicator rtds-connection-indicator--${status}`}
                        role="status"
                        aria-label={connectionIndicatorLabel}
                        title={connectionIndicatorLabel}
                    />
                )}
                <div className="rtds-controls">
                    <DatePicker 
                        value={dayjs(currentDate)} 
                        onChange={handleDateChange}
                        className="rtds-date-picker"
                        popupClassName="rtds-date-picker-dropdown"
                    />
                    <span style={{ color: '#fff', fontSize: 20, fontWeight: 600, marginLeft: 8 }}>{getWeekday(currentDate)}</span>
                    {getStatusTag()}
                    <Button 
                        size="small" 
                        onClick={handleReload}
                        style={{ marginLeft: 8 }}
                    >
                        Reload
                    </Button>
                </div>
            </div>
            {error ? (
                <div className="error-message">{error}</div>
            ) : Object.keys(matches).length === 0 ? (
                <div className="no-matches" style={{ color: '#fff', textAlign: 'center', padding: '20px' }}>No matches scheduled for this date</div>
            ) : (
                Object.entries(matches)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([court, courtMatches]) => (
                        <Court key={court} court={court} matches={courtMatches} inProgressRoundMaxByCategory={inProgressRoundMaxByCategory} />
                    ))
            )}
        </div>
    );
}

// Create a wrapper component that applies the dark theme
const RtdsWithTheme = () => (
  <ConfigProvider
    theme={{
      algorithm: theme.darkAlgorithm,
    }}
  >
    <RTDS />
  </ConfigProvider>
);

export default RtdsWithTheme;
